import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Store,
  Users,
  Coins,
  TrendingDown,
  Shield,
  ShieldCheck,
  ArrowRightLeft,
  Clock,
  BadgeCheck,
  BarChart3,
  ExternalLink,
  Star,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SellerCard({ seller }) {
  const listedDate = new Date(seller.listed_at);
  const daysAgo = Math.floor((Date.now() - listedDate.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div
      className="p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all"
      data-testid={`seller-card-${seller.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#0F2744] flex items-center justify-center text-white font-bold text-sm">
            {seller.display_name.substring(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-slate-900">{seller.display_name}</span>
              {seller.verified && (
                <BadgeCheck className="w-4 h-4 text-blue-500" />
              )}
            </div>
            <span className="text-xs text-slate-500 font-mono">{seller.wallet_address}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-amber-500">
          <Star className="w-3.5 h-3.5 fill-amber-400" />
          <span className="text-sm font-semibold">{seller.reputation_score}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Token</p>
          <p className="font-bold text-slate-900">{seller.token_symbol}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Quantity</p>
          <p className="font-bold text-slate-900">{seller.tokens_listed.toLocaleString()}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Ask Price</p>
          <p className="font-bold text-emerald-600">${seller.asking_price.toLocaleString()}</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">Total Value</p>
          <p className="font-bold text-slate-900">
            ${(seller.tokens_listed * seller.asking_price).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <ArrowRightLeft className="w-3 h-3" /> {seller.trades_completed} trades
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> {daysAgo}d ago
          </span>
          <span className="flex items-center gap-1 text-slate-600">
            {seller.project_name}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="text-xs border-[#0F2744] text-[#0F2744] hover:bg-[#0F2744] hover:text-white"
          onClick={() => toast.info("P2P trading will be available after project launch.")}
          data-testid={`buy-from-seller-${seller.id}`}
        >
          <ExternalLink className="w-3 h-3 mr-1" /> Buy
        </Button>
      </div>
    </div>
  );
}

export default function SellTokensPage() {
  const [sellers, setSellers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sellersRes, statsRes] = await Promise.all([
        axios.get(`${API}/marketplace/sellers`),
        axios.get(`${API}/marketplace/sell-stats`),
      ]);
      setSellers(sellersRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load marketplace data");
    } finally {
      setLoading(false);
    }
  };

  const filteredSellers =
    filterProject === "all"
      ? sellers
      : sellers.filter((s) => s.project_id === filterProject);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="sell-tokens-loading">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sell-tokens-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Token Marketplace</h1>
        <p className="text-slate-500 mt-1">
          Browse tokens listed for sale by verified holders on the blockchain
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-slate-200" data-testid="stat-total-listings">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E8F4FD] rounded-xl flex items-center justify-center">
                <Store className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Active Listings</p>
                <p className="text-xl font-bold text-slate-900">{stats.total_listings}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-total-tokens">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Coins className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Tokens Listed</p>
                <p className="text-xl font-bold text-slate-900">
                  {stats.total_tokens_listed.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-market-value">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Market Value</p>
                <p className="text-xl font-bold text-slate-900">
                  ${(stats.total_market_value / 1000000).toFixed(2)}M
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-avg-price">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Avg Ask Price</p>
                <p className="text-xl font-bold text-slate-900">
                  ${stats.average_price.toLocaleString()}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-verified-sellers">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Verified Sellers</p>
                <p className="text-xl font-bold text-slate-900">{stats.verified_sellers}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Per-project breakdown */}
      {stats?.projects && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.projects.map((proj) => (
            <Card key={proj.project_id} className="border-slate-200" data-testid={`project-stats-${proj.token_symbol}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-900">{proj.project_name}</p>
                    <span className="text-xs font-mono text-slate-500">{proj.token_symbol}</span>
                  </div>
                  <span className="px-2 py-1 bg-[#0F2744] text-white text-xs rounded-full">
                    {proj.listings} listings
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-slate-500">Total Tokens</p>
                    <p className="font-semibold">{proj.total_tokens.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Min Price</p>
                    <p className="font-semibold text-emerald-600">${proj.min_price}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Max Price</p>
                    <p className="font-semibold text-red-500">${proj.max_price}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filter + Listings */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#3B82F6]" /> Blockchain Seller Listings
          </CardTitle>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-48" data-testid="project-filter">
              <SelectValue placeholder="Filter by project" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              <SelectItem value="proj-broham-development">BRHM - Broham</SelectItem>
              <SelectItem value="proj-rustenburg-mining">RPTM - Rustenburg</SelectItem>
              <SelectItem value="proj-northern-cape-solar">NCSL - Northern Cape</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredSellers.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="no-sellers">
                <Store className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">No listings found</p>
                <p className="text-sm">Try changing the filter above</p>
              </div>
            ) : (
              filteredSellers.map((seller) => (
                <SellerCard key={seller.id} seller={seller} />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50" data-testid="marketplace-info">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Blockchain-Secured Trading</p>
              <p className="text-sm text-blue-700 mt-1">
                All token listings are verified on the blockchain. Sellers are rated by reputation
                score and trade history. Transactions are secured through smart contract escrow,
                ensuring safe peer-to-peer token transfers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
